import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import { 
  MapPin, Mail, Phone, ArrowLeft, Briefcase, DollarSign, 
  Globe, Calendar, Clock, User, Star, Download, Eye,
  Building, Languages
} from 'lucide-react';
import { Badge } from '../components/ui/badge';

const ProspectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [prospect, setProspect] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('sourcing'); // 'sourcing' or 'application'

  useEffect(() => {
    // Check if we're viewing from My Candidates (application context)
    const fromCandidates = window.location.pathname.startsWith('/prospects/');
    setViewMode(fromCandidates ? 'application' : 'sourcing');
    
    if (fromCandidates && userProfile?.organizationId) {
      fetchProspectWithApplication();
    } else {
      fetchProspectDetails();
    }
  }, [id, userProfile]);

  const fetchProspectDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/${id}`);
      
      if (response.data.success) {
        setProspect(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching prospect details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProspectWithApplication = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${id}/application/${userProfile.organizationId}`
      );
      
      if (response.data.success) {
        setProspect(response.data.data.prospect);
        setApplications(response.data.data.applications || []);
      }
    } catch (error) {
      console.error('Error fetching prospect application data:', error);
      // Fallback to regular prospect view
      fetchProspectDetails();
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${applicationId}/status`,
        { status: newStatus }
      );
      
      // Update local state
      setApplications(applications.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
      
      alert('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleNotesUpdate = async (applicationId, notes) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${applicationId}/notes`,
        { notes }
      );
      
      alert('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Loading prospect details...</p>
        </div>
      </div>
    );
  }

  if (!prospect) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Prospect not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(viewMode === 'application' ? '/candidates' : '/sourcing')}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-medium text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          {viewMode === 'application' ? 'Back to Candidates' : 'Back to Sourcing'}
        </button>

        {/* Application Info Banner - Only show when viewing from My Candidates */}
        {viewMode === 'application' && applications.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">
                  Application: {applications[0].positions?.title}
                </h3>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>Applied: {new Date(applications[0].applied_at).toLocaleDateString()}</p>
                  <p>Status: <span className="font-medium capitalize">{applications[0].status}</span></p>
                  {applications[0].positions?.location && (
                    <p>Location: {applications[0].positions.location}</p>
                  )}
                  {applications[0].positions?.salary_min && (
                    <p>
                      Salary: €{applications[0].positions.salary_min.toLocaleString()} 
                      {applications[0].positions.salary_max && ` - €${applications[0].positions.salary_max.toLocaleString()}`}
                    </p>
                  )}
                </div>
              </div>
              <select
                value={applications[0].status}
                onChange={(e) => handleStatusUpdate(applications[0].id, e.target.value)}
                className="px-3 py-1 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="reviewing">Reviewing</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interviewed">Interviewed</option>
                <option value="offered">Offered</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
                <option value="accepted">Accepted</option>
              </select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start gap-4 mb-6">
                {/* Profile Picture */}
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-xl">
                    {prospect.firstName?.[0]}{prospect.lastName?.[0]}
                  </span>
                </div>

                {/* Basic Info */}
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">
                    {prospect.lastName}, {prospect.firstName}
                  </h1>
                  <p className="text-gray-600 mb-3">
                    {prospect.currentJobTitle || prospect.jobTitle || 'Job Title Not Specified'}
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span>
                    € {prospect.expectedSalaryMin ? prospect.expectedSalaryMin.toLocaleString() : '0'}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="capitalize">{prospect.workType || 'Full-time'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Languages className="w-4 h-4 text-gray-400" />
                  <span>{prospect.languages?.join(', ') || 'Not specified'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{prospect.location || 'Location not specified'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Building className="w-4 h-4 text-gray-400" />
                  <span className="capitalize">{prospect.workArrangement || 'Hybrid'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="capitalize">{prospect.experienceLevel || 'Middle'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  <span>{prospect.currentCompany || 'Not specified'}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{prospect.availability || '1 Month'}</span>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
              <div className="space-y-3">
                {prospect.resumeUrl ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-700 font-medium">CV</span>
                      <span className="text-gray-500 text-sm">CV_{prospect.lastName}_{prospect.firstName}.pdf</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={prospect.resumeUrl}
                        download
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        download
                      </a>
                      <a
                        href={prospect.resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        preview
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">CV - Not uploaded</div>
                )}

                {prospect.diplomaUrl ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-700 font-medium">Diplom</span>
                      <span className="text-gray-500 text-sm">Diploma_{prospect.lastName}_{prospect.firstName}.pdf</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={prospect.diplomaUrl}
                        download
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        download
                      </a>
                      <a
                        href={prospect.diplomaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm underline"
                      >
                        preview
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">Diplom - Not uploaded</div>
                )}
              </div>
            </div>

            {/* Application-Specific Sections - Only show when viewing from My Candidates */}
            {viewMode === 'application' && applications.length > 0 && (
              <>
                {/* Cover Letter */}
                {applications[0].cover_letter && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Cover Letter</h2>
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                      {applications[0].cover_letter}
                    </p>
                  </div>
                )}

                {/* Company-Specific Notes */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Internal Notes (Private to Your Company)</h2>
                  <p className="text-sm text-gray-500 mb-3">
                    These notes are only visible to your organization and will not be shared with the candidate or other companies.
                  </p>
                  <textarea
                    defaultValue={applications[0].notes || ''}
                    onBlur={(e) => handleNotesUpdate(applications[0].id, e.target.value)}
                    placeholder="Add notes about this candidate, interview feedback, recommendations, etc."
                    className="w-full h-32 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Notes are auto-saved when you click outside the text area
                  </p>
                </div>
              </>
            )}

            {/* Skills */}
            {prospect.skills && prospect.skills.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {prospect.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* About */}
            {prospect.bio && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">About</h2>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {prospect.bio}
                </p>
              </div>
            )}

            {/* Experience / Expectations */}
            {prospect.experienceSummary && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Experience / Expectations</h2>
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                  {prospect.experienceSummary}
                </p>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* ID and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-gray-500">ID {prospect.id?.substring(0, 5)}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(prospect.createdAt || Date.now()).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Mail className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Download className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Star className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                  <option>Active</option>
                  <option>Contacted</option>
                  <option>Interview Scheduled</option>
                  <option>Hired</option>
                </select>
              </div>
            </div>

            {/* Event */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Event</h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Interview Hiring Manager</p>
                    <p className="text-xs text-gray-600">Jupiter GmbH</p>
                    <p className="text-xs text-gray-500">08.07.2024 10:00 - 11:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Application */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Application</h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Frontend Developer</p>
                    <p className="text-xs text-gray-600">Jupiter GmbH</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Also Suitable */}
            {prospect.preferredJobTitles && prospect.preferredJobTitles.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Also suitable</h3>
                <div className="space-y-1">
                  {prospect.preferredJobTitles.map((title, idx) => (
                    <p key={idx} className="text-sm text-gray-700">{title}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Notes</h3>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                rows={4}
                placeholder="Add notes about this prospect..."
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium">
                Present Position
              </button>
              <button className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md text-sm font-medium">
                Contact Prospect
              </button>
              <button className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-md text-sm font-medium">
                Save to Candidates
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectDetail;
